import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth-token');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

export interface BlogPost {
  id: string;
  title: string;
  content: string;
  source: 'medium' | 'linkedin';
  url: string;
  publishedAt: string;
  author: string;
  tags: string[];
  preview?: string;
  estimatedReadTime?: number;
}

export const blogApi = {
  async importFromMedium(url: string): Promise<BlogPost> {
    try {
      if (!url.includes('medium.com')) {
        throw new Error('Invalid Medium URL');
      }

      // In development, simulate API call
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return {
          id: Date.now().toString(),
          title: 'Sample Medium Post',
          content: '## Sample Content\n\nThis is a sample Medium post content...',
          source: 'medium',
          url,
          publishedAt: new Date().toISOString(),
          author: 'John Doe',
          tags: ['programming', 'technology'],
          preview: 'This is a preview of the Medium article...',
          estimatedReadTime: 5
        };
      }

      const response = await api.post('/blogs/import/medium', { url });
      return response.data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to import from Medium');
    }
  },

  async importFromLinkedIn(url: string): Promise<BlogPost> {
    try {
      if (!url.includes('linkedin.com/pulse')) {
        throw new Error('Invalid LinkedIn article URL');
      }

      // In development, simulate API call
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return {
          id: Date.now().toString(),
          title: 'Sample LinkedIn Article',
          content: '## Sample Content\n\nThis is a sample LinkedIn article content...',
          source: 'linkedin',
          url,
          publishedAt: new Date().toISOString(),
          author: 'Jane Smith',
          tags: ['career', 'technology'],
          preview: 'This is a preview of the LinkedIn article...',
          estimatedReadTime: 3
        };
      }

      const response = await api.post('/blogs/import/linkedin', { url });
      return response.data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to import from LinkedIn');
    }
  },

  async getAll(): Promise<BlogPost[]> {
    try {
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return [
          {
            id: '1',
            title: 'Understanding Big O Notation',
            content: '## Big O Notation\n\nIn computer science...',
            source: 'medium',
            url: 'https://medium.com/example/big-o',
            publishedAt: new Date().toISOString(),
            author: 'John Doe',
            tags: ['algorithms', 'computer-science'],
            preview: 'Learn about time complexity and algorithm analysis...',
            estimatedReadTime: 5
          },
          {
            id: '2',
            title: 'Career Growth in Tech',
            content: '## Career Development\n\nNavigating your tech career...',
            source: 'linkedin',
            url: 'https://linkedin.com/pulse/career-growth',
            publishedAt: new Date().toISOString(),
            author: 'Jane Smith',
            tags: ['career', 'technology'],
            preview: 'Tips for growing your career in technology...',
            estimatedReadTime: 4
          }
        ];
      }

      const response = await api.get('/blogs');
      return response.data;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch blogs');
    }
  }
};